import React, { useMemo } from 'react';
import { getCurrencySymbol } from "../../../../tenant/currency";
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useGetWorkflowStatusesQuery } from '../../../../service/customer/workflow-statuses-service';
import { RootState } from '../../../../stores/store';
import { NotificationType } from '../../../../model/notifications/notifications';
import { RepaymentLoanResponse } from '../../../../model/charge';
import styles from './NotificationTrackline.module.scss';
import { ApplicationRoutes } from '../../../../routes';

interface Props {
  loanRepaymentData?: RepaymentLoanResponse;
}

interface TrackItem {
  id: string;
  label: string;
  amount?: string;
  date?: string;
  actionText: string;
  onAction: () => void;
}

const GreenDot: React.FC = () => <span className={styles.greenDot} />;

const formatCurrency = (amount: number, currency?: string) => {
  const symbol = getCurrencySymbol(currency);
  return `${symbol}${new Intl.NumberFormat('en-US').format(amount)}`;
};

const formatDate = (dateStr: string) => {
  return new Date(dateStr).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
};

export const NotificationTrackline = React.memo<Props>(({ loanRepaymentData }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const notifications = useSelector((state: RootState) => state.notifications.notifications);
  const { data: workflowStatuses } = useGetWorkflowStatusesQuery();

  const items = useMemo<TrackItem[]>(() => {
    const result: TrackItem[] = [];

    // 1. Next repayment installment (mirrors NextRepaymentCard on mobile)
    const nextBill = loanRepaymentData?.billPaymentProcesses?.[0];
    if (nextBill) {
      result.push({
        id: 'repayment',
        label: t('loanRepayment.nextRepayment'),
        amount: formatCurrency(nextBill.totalAmount.amount, nextBill.totalAmount.currency),
        date: formatDate(nextBill.startDate),
        actionText: t('common.labels.payNowKey'),
        onAction: () => navigate(`/${ApplicationRoutes.CUSTOMER_TRANSACTION_HISTORY}`),
      });
    }

    // 2. Active order workflow statuses (mirrors active orders section of WorkflowStatusesSlider)
    workflowStatuses?.activeOrderWorkflowStatuses?.forEach(ws => {
      result.push({
        id: `order-${ws.workflowId}`,
        label: `${t('workflow.yourOrderIsInThisStatus')} ${ws.userFriendlyStatus}`,
        actionText: t('history.view'),
        onAction: () => navigate(`/${ApplicationRoutes.CUSTOMER_TRANSACTION_HISTORY}`),
      });
    });

    // 3. BPS billing notifications from SignalR (mirrors billingDashNotifications in WorkflowStatusesSlider)
    notifications
      .filter(n => Number(n.type) === NotificationType.BpsBilling || n.type === 'BpsBilling')
      .slice(0, 3)
      .forEach(n => {
        const rawAmount = n.data?.amount;
        const rawDate = n.data?.dueDate;
        result.push({
          id: n.id,
          label: n.data?.notificationTypeText ?? n.title,
          amount: rawAmount != null ? formatCurrency(Number(rawAmount)) : undefined,
          date: rawDate ? formatDate(rawDate) : undefined,
          actionText: t('common.labels.payNowKey'),
          onAction: () => navigate(`/${ApplicationRoutes.BUY_ELECTRICITY}`),
        });
      });

    return result;
  }, [loanRepaymentData, workflowStatuses, notifications, navigate, t]);

  if (items.length === 0) return null;

  return (
    <div className={styles.container}>
      {items.map(item => (
        <div key={item.id} className={styles.trackItem}>
          <div className={styles.left}>
            <GreenDot />
            <div className={styles.info}>
              <span className={styles.label}>{item.label}</span>
              {(item.amount || item.date) && (
                <div className={styles.valueRow}>
                  {item.amount && <span className={styles.amount}>{item.amount}</span>}
                  {item.date && <span className={styles.date}>{item.date}</span>}
                </div>
              )}
            </div>
          </div>
          <button className={styles.actionButton} onClick={item.onAction}>
            {item.actionText}
          </button>
        </div>
      ))}
    </div>
  );
});
